import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Row } from "reactstrap/lib";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { ValidationSummary, InputFL } from "Components/Inputs";
import { Modal, ModalBody, ModalHeader, ModalProps, Button, SpinningButton } from "Components/ReactstrapSlim";
import { PortalClient, SupportRequest } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import AccountContext from "../../AccountContext";

export interface SupportModalProps extends ModalProps {
   closeModal: () => void;
   onRequested?: () => void;
}

function getEditModel() {
   return {
      message: ""
   };
}

export type EditModel = ReturnType<typeof getEditModel>;

const EditModelSchema: yup.SchemaOf<EditModel> = yup.object({
   message: yup.string().trim().required("Message is required").defined()
}).defined();

export function useSupportModal({ isOpen, closeModal, onRequested }: Pick<SupportModalProps, "isOpen" | "closeModal" | "onRequested">) {
   const [isSaving, setSaving] = useState(false);
   const portalClient = useApiClient(PortalClient);
   const account = useContext(AccountContext);
   const [serverError, setServerError] = useState<Array<[string, string]>>([]);

   const formDefaultValues: EditModel = useMemo(() => getEditModel(), []);

   const formMethods = useForm({
      defaultValues: formDefaultValues,
      resolver: yupResolver(EditModelSchema)
   })

   const closeModalInternal = () => {            
      closeModal();
   }

   async function onSubmit(data) {
      setSaving(true);

      try {
         const model = new SupportRequest({ message: data.message });
         const response = await portalClient.supportRequest(account.id, model);

         if (response.success) {
            if(onRequested)
               onRequested();            
         } else {
            setServerError([["message", response.errorMessage || "Unexpected server error"]]);
         }
      }
      catch (e: any) {
         if (e.error) {
            setServerError([["message", e.error]]);
         }
         console.log(e);
      }
      finally {
         setSaving(false);
      }
   }

   useEffect(() => {
      formMethods.reset(formDefaultValues);
   }, [isOpen]);

   return { closeModalInternal, account, formMethods, onSubmit, serverError, isSaving }
}

const SupportModal: React.FC<SupportModalProps> = function supportModal({ closeModal, onRequested, isOpen, ...modalProps }) {
   const { closeModalInternal, formMethods, onSubmit, serverError, isSaving } = useSupportModal({ isOpen, closeModal, onRequested });

   return (
      <Modal className="modal-lg support-modal" toggle={closeModalInternal} isOpen={isOpen} {...modalProps}>
         <ModalHeader toggle={closeModalInternal} lightWordsCount={2}>
            How can we help?
         </ModalHeader>
         <ModalBody>
            <div className="p2 tc-secondary">Send a message to the LiquidSpace support team to let us know how we can help.</div>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
               <InputFL name="message" control={formMethods.control} placeholder="Message" type="textarea" />
               <ValidationSummary errors={formMethods.formState.errors} fields={["message"]} serverErrors={serverError} />
               <Row className="d-flex justify-content-end support-modal-buttons">
                  <Button onClick={(e) => { e.preventDefault(); closeModalInternal(); }} outline color="gray" size="lg" className="mr-3 mt-3">Back</Button>
                  <SpinningButton isLoading={isSaving} color="orange" size="lg" className="ml-3 mt-3">Send Message</SpinningButton>                  
               </Row>
            </form>
         </ModalBody>
      </Modal>
   );
}

export default SupportModal;